import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { toast } from 'sonner';
import TopBar from '@/components/TopBar';
import { useTheme } from '@/components/ThemeProvider';
import { API } from '@/utils/apiConfig';

// Section components
import { SeoHead } from '@/components/home/SeoHead';
import { Header } from '@/components/home/Header';
import { HeroSection } from '@/components/home/HeroSection';
import { StatsSection } from '@/components/home/StatsSection';
import { PartnersSection } from '@/components/home/PartnersSection';
import { AboutSection } from '@/components/home/AboutSection';
import { ServicesSection } from '@/components/home/ServicesSection';
import { ProjectsSection } from '@/components/home/ProjectsSection';
import { TeamSection } from '@/components/home/TeamSection';
import { FaqSection } from '@/components/home/FaqSection';
import { ContactSection } from '@/components/home/ContactSection';
import { EnergoPromo } from '@/components/home/EnergoPromo';
import { Footer } from '@/components/home/Footer';
import { ProjectModal } from '@/components/home/ProjectModal';

const Home = () => {
  useTranslation();
  const { theme } = useTheme();
  const [stats, setStats] = useState({ installations: 200, customers: 52, engineers: 14, partners: 26 });
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [partners, setPartners] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectCategories, setProjectCategories] = useState([]);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [siteSettings, setSiteSettings] = useState({ site_url: 'https://www.solar-green.cz', favicon_url: '/favicon.ico' });
  const [topbarLinks, setTopbarLinks] = useState({ nav_extra_url: '', nav_extra_label: '' });
  const [pageSections, setPageSections] = useState([]);
  const [siteContent, setSiteContent] = useState({});
  const [pageLoaded, setPageLoaded] = useState(false);

  // Clear stale caches on mount
  useEffect(() => {
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('sg_cache_')) sessionStorage.removeItem(key);
      }
      if (!window.location.pathname.startsWith('/admin')) {
        localStorage.removeItem('managed_site_slug');
        localStorage.removeItem('managed_site_id');
      }
    } catch {}
  }, []);

  // Helper: get text from DB content
  const txt = (sectionId, key, fallback = '') => {
    const lang = (i18n.language || '').startsWith('en') ? 'en' : 'cs';
    let dbVal = siteContent[`${sectionId}.${key}`];
    if (!dbVal) {
      const sec = pageSections.find(s => s.id === sectionId || s.menu_id === sectionId);
      if (sec && sec.type !== sectionId) dbVal = siteContent[`${sec.type}.${key}`];
    }
    if (dbVal) { const val = lang === 'en' ? (dbVal.en || dbVal.cs) : dbVal.cs; if (val) return val; }
    return fallback;
  };

  // Scroll tracking
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      const sectionIds = pageSections.length > 0 ? pageSections.map(s => s.menu_id || s.id) : ['home', 'about', 'services', 'projects', 'faq', 'contact'];
      const scrollPosition = window.scrollY + 150;
      for (const sid of sectionIds) {
        const element = document.getElementById(sid);
        if (element && scrollPosition >= element.offsetTop && scrollPosition < element.offsetTop + element.offsetHeight) {
          setActiveSection(sid); break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [pageSections]);

  const loadRecaptchaScript = useCallback((siteKey) => {
    if (!siteKey || document.getElementById('recaptcha-script')) return;
    const script = document.createElement('script');
    script.id = 'recaptcha-script';
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    document.body.appendChild(script);
  }, []);

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { if (recaptchaSiteKey) loadRecaptchaScript(recaptchaSiteKey); }, [recaptchaSiteKey, loadRecaptchaScript]);
  useEffect(() => {
    if (selectedCategory === 'all') setFilteredProjects(projects);
    else setFilteredProjects(projects.filter(p => p.category === selectedCategory));
  }, [selectedCategory, projects]);

  // Google Analytics
  useEffect(() => {
    const gaId = siteSettings.google_analytics_id;
    if (!gaId || document.querySelector(`script[src*="gtag/js?id=${gaId}"]`)) return;
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    script.async = true;
    document.head.appendChild(script);
    const inline = document.createElement('script');
    inline.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${gaId}');`;
    document.head.appendChild(inline);
  }, [siteSettings.google_analytics_id]);

  const fetchData = async () => {
    try {
      const [statsRes, servicesRes, projectsRes, faqsRes, partnersRes, recaptchaRes, companyRes, teamRes, categoriesRes, siteSettingsRes, sectionsRes, contentRes] = await Promise.all([
        axios.get(`${API}/stats`), axios.get(`${API}/services`), axios.get(`${API}/projects`),
        axios.get(`${API}/faq`), axios.get(`${API}/partners`),
        axios.get(`${API}/settings/recaptcha-key`), axios.get(`${API}/company`), axios.get(`${API}/team`),
        axios.get(`${API}/categories`), axios.get(`${API}/settings/site`),
        axios.get(`${API}/page-sections`), axios.get(`${API}/site-content`)
      ]);
      setStats(statsRes.data); setServices(servicesRes.data); setProjects(projectsRes.data);
      setFilteredProjects(projectsRes.data); setFaqs(faqsRes.data); setPartners(partnersRes.data);
      if (recaptchaRes.data.site_key) setRecaptchaSiteKey(recaptchaRes.data.site_key);
      setCompanyInfo(companyRes.data); setTeamMembers(teamRes.data); setProjectCategories(categoriesRes.data);
      setSiteSettings(siteSettingsRes.data);
      try { sessionStorage.setItem('sg_loader_ms', String(siteSettingsRes.data.loader_duration_ms || 500)); } catch {}
      try { const tb = await axios.get(`${API}/settings/topbar`); setTopbarLinks(tb.data); } catch {}
      setPageSections(sectionsRes.data.filter(s => s.visible));
      const cMap = {};
      contentRes.data.forEach(c => { cMap[`${c.section}.${c.key}`] = { cs: c.value_cs, en: c.value_en }; });
      setSiteContent(cMap);
      setPageLoaded(true);
    } catch (error) { console.error('Error fetching data:', error); setPageLoaded(true); }
  };

  const getRecaptchaToken = async () => {
    if (!recaptchaSiteKey || !window.grecaptcha) return null;
    try {
      await new Promise((resolve) => { if (window.grecaptcha?.ready) window.grecaptcha.ready(resolve); else resolve(); });
      return await window.grecaptcha.execute(recaptchaSiteKey, { action: 'contact' });
    } catch (error) { console.error('reCAPTCHA error:', error); return null; }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const recaptchaToken = await getRecaptchaToken();
      await axios.post(`${API}/contact`, { ...formData, recaptcha_token: recaptchaToken });
      toast.success(txt('contact', 'success'));
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      if (error.response?.data?.detail === 'reCAPTCHA verification failed') toast.error(txt('contact', 'recaptcha_fail'));
      else toast.error(txt('contact', 'error'));
    } finally { setIsSubmitting(false); }
  };

  const scrollToSection = (id) => { document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); setMobileMenuOpen(false); };

  const menuItems = pageSections.length > 0
    ? [...pageSections.filter(s => s.in_menu).map(s => ({ id: s.menu_id || s.id, label: (i18n.language || '').startsWith('en') ? (s.menu_label_en || s.menu_label_cs) : s.menu_label_cs, link: s.link || null })), { id: 'blog', label: 'Blog', link: '/blog' }]
    : [{ id: 'home', label: txt('nav', 'home') }, { id: 'about', label: txt('nav', 'about') }, { id: 'services', label: txt('nav', 'services') }, { id: 'projects', label: txt('nav', 'projects') }, { id: 'faq', label: txt('nav', 'faq') }, { id: 'contact', label: txt('nav', 'contact') }, { id: 'blog', label: 'Blog', link: '/blog' }];

  const getMenuStyle = (itemId) => {
    const isActive = activeSection === itemId;
    const isHovered = hoveredMenu === itemId;
    if (isActive) return { backgroundColor: theme.menu_active_bg, color: theme.menu_active_text, transform: 'translateY(-1px)' };
    if (isHovered) return { backgroundColor: `${theme.primary_color}15`, color: theme.primary_color, transform: 'translateY(-1px)' };
    return { backgroundColor: 'transparent', color: theme.menu_text, transform: 'translateY(0)' };
  };

  const isEn = (i18n.language || '').startsWith('en');
  const metaTitle = isEn ? (siteSettings.meta_title_en || siteSettings.meta_title_cs) : (siteSettings.meta_title_cs || siteSettings.meta_title_en);
  const metaDesc = isEn ? (siteSettings.meta_description_en || siteSettings.meta_description_cs) : (siteSettings.meta_description_cs || siteSettings.meta_description_en);
  const metaKeywords = isEn ? (siteSettings.meta_keywords_en || siteSettings.meta_keywords_cs) : (siteSettings.meta_keywords_cs || siteSettings.meta_keywords_en);

  // Loader removal
  const [loaderDone, setLoaderDone] = useState(false);
  useEffect(() => {
    let duration = 500;
    try { const cached = sessionStorage.getItem('sg_loader_ms'); if (cached) duration = parseInt(cached); } catch {}
    const minTime = setTimeout(() => setLoaderDone(true), duration);
    return () => clearTimeout(minTime);
  }, []);
  useEffect(() => {
    if (pageLoaded && loaderDone) {
      const loader = document.getElementById('initial-loader');
      if (loader) { loader.style.opacity = '0'; loader.style.transition = 'opacity 0.4s'; setTimeout(() => loader.remove(), 400); }
    }
  }, [pageLoaded, loaderDone]);

  if (!pageLoaded || !loaderDone) return null;

  // Section renderer
  const renderSection = (section) => {
    const sid = section.menu_id || section.id;
    if (section.type === 'link') return null;
    switch (section.type) {
      case 'hero': return <HeroSection key={section.id} sid={sid} theme={theme} txt={txt} scrollToSection={scrollToSection} stats={stats} />;
      case 'stats': return <StatsSection key={section.id} sid={sid} theme={theme} stats={stats} txt={txt} />;
      case 'partners': return <PartnersSection key={section.id} sid={sid} theme={theme} partners={partners} />;
      case 'about': return <AboutSection key={section.id} sid={sid} theme={theme} txt={txt} />;
      case 'services': return <ServicesSection key={section.id} sid={sid} theme={theme} services={services} txt={txt} />;
      case 'projects': return <ProjectsSection key={section.id} sid={sid} theme={theme} txt={txt} filteredProjects={filteredProjects} projectCategories={projectCategories} selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory} setSelectedProject={setSelectedProject} />;
      case 'team': return <TeamSection key={section.id} sid={sid} theme={theme} txt={txt} teamMembers={teamMembers} />;
      case 'faq': return <FaqSection key={section.id} sid={sid} theme={theme} faqs={faqs} txt={txt} />;
      case 'contact': return <ContactSection key={section.id} sid={sid} theme={theme} txt={txt} companyInfo={companyInfo} siteSettings={siteSettings} formData={formData} setFormData={setFormData} handleSubmit={handleSubmit} isSubmitting={isSubmitting} recaptchaSiteKey={recaptchaSiteKey} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" style={{ fontFamily: `${theme.font_body}, sans-serif` }}>
      <SeoHead siteSettings={siteSettings} metaTitle={metaTitle} metaDesc={metaDesc} metaKeywords={metaKeywords} isEn={isEn} services={services} companyInfo={companyInfo} faqs={faqs} />

      <div className="fixed top-0 left-0 right-0 z-50">
        <TopBar />
        <Header theme={theme} isScrolled={isScrolled} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} menuItems={menuItems} activeSection={activeSection} hoveredMenu={hoveredMenu} setHoveredMenu={setHoveredMenu} scrollToSection={scrollToSection} topbarLinks={topbarLinks} siteSettings={siteSettings} getMenuStyle={getMenuStyle} i18n={i18n} />
      </div>

      {pageSections.map(renderSection)}

      <EnergoPromo theme={theme} />
      <Footer theme={theme} txt={txt} companyInfo={companyInfo} siteSettings={siteSettings} scrollToSection={scrollToSection} />

      <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} theme={theme} txt={txt} projectCategories={projectCategories} />
    </div>
  );
};

export default Home;
